import { IStaffDetails } from "../../../../@types/admin/AdminStaff";
import { useReactivateStaff } from "../../../../hooks/mutations/admin/staff";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  FilterButton,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
} from "../AdminPage.styles";

interface IReactivateStaffModalProps {
  staff: IStaffDetails;
  /** Shown in the prompt so the operator can confirm who this is. */
  staffName?: string;
  onClose: () => void;
}

/**
 * Confirmation for re-activating a staff member (EN-5150 review). Retool fired
 * the PUT straight off the button; this restores an account's access to
 * patient data, so it gets the same confirm-then-act treatment as the other
 * /admin actions.
 */
export function ReactivateStaffModal({
  staff,
  staffName,
  onClose,
}: IReactivateStaffModalProps) {
  const { showToast } = useToast();
  const { reactivateStaff, isReactivatingStaff } = useReactivateStaff();

  const handleReactivate = async () => {
    try {
      await reactivateStaff({ staffId: staff.id, userId: staff.userId });
      showToast("The staff member was re-activated.", "success");
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error re-activating this staff member."
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Re-activate staff</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Re-activate <strong>{staffName || `staff #${staff.id}`}</strong>? This
          re-enables their sign-in and restores their access to patient data.
        </ModalBodyText>
        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isReactivatingStaff}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isReactivatingStaff}
            onClick={handleReactivate}
          >
            {isReactivatingStaff ? "Re-activating..." : "Re-activate"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
